import { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { RELYING_PARTIES_URL } from "@constants/routes";
import { resources } from "@constants/hardcoded-strings";
import { ModalTypes } from "@enums/modal-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";
import { useDiscardChangesModal } from "@hooks/use-discard-changes-modal";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { FormattedRelyingPartyConfig } from "@idw-types/data/relying-party-config";
import { updateRelyingPartyReduxRequest } from "@redux-requests/relying-party-details";
import { navigateToUtil } from "@utils/navigation-util";
import { getRelyingPartyDetailsValidationErrors } from "@utils/validation/relying-party/relying-party-details/relying-party-details-validation-util";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { TabContainer } from "@containers/common/tab-container/tab-container";
import { RelyingPartyDetailsGeneralTab } from "@containers/relying-party-details/relying-party-details-component/relying-party-details-general-tab/relying-party-details-general-tab";
import { RelyingPartyDetailsCustomHostnamesTab } from "@containers/relying-party-details/relying-party-details-component/relying-party-details-custom-hostnames-tab/relying-party-details-custom-hostnames-tab";
import styles from "./relying-party-details-component.less";

type RelyingPartyDetailsComponentProps = {
    relyingParty: FormattedRelyingPartyConfig;
    axnCompanies: Array<AxnCompany>;
};

export const RelyingPartyDetailsComponent = (props: RelyingPartyDetailsComponentProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { relyingParty, axnCompanies } = props;
    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [relyingPartyState, setRelyingPartyState] = useState<FormattedRelyingPartyConfig>(relyingParty);

    useEffect(() => {
        setRelyingPartyState(cloneDeep(relyingParty));
    }, [relyingParty]);

    const stateIsUnchanged = useMemo(() => {
        return isEqual(relyingParty, relyingPartyState);
    }, [relyingParty, relyingPartyState]);

    useDiscardChangesModal({
        modalIdPrefix: "discard-relying-party-details-changes",
        modalType: ModalTypes.DiscardRelyingPartyChanges,
        stateIsUnchanged
    });

    const onCloseClick = () => {
        navigateToUtil(RELYING_PARTIES_URL, navigate, routerLocation.pathname);
    };

    const handleSubmit = async () => {
        setRequestInProgress(true);
        await updateRelyingPartyReduxRequest(dispatch)(relyingPartyState, axnCompanies);
        setRequestInProgress(false);
    };

    const relyingPartyDetailsValidationErrors = useMemo(() => {
        return getRelyingPartyDetailsValidationErrors(relyingPartyState);
    }, [relyingPartyState]);

    return (
        <div className={styles.relyingPartyDetailsComponent}>
            <div className={styles.headerContainer}>
                <span className={styles.relyingPartyName}>
                    {`${relyingPartyState.name}`}
                </span>
                <div className={styles.buttonContainer}>
                    <Button
                        id="relying-party-details-close-button"
                        onClick={onCloseClick}
                        variant={"outline-secondary"}
                    >
                        {resources.common.buttons.close.label}
                    </Button>
                    {relyingParty.permissions.canModify && (
                        <ButtonWithSpinner
                            id="relying-party-details-submit-button"
                            disabled={requestInProgress || stateIsUnchanged || relyingPartyDetailsValidationErrors.count > 0}
                            hideSpinner={stateIsUnchanged || relyingPartyDetailsValidationErrors.count > 0}
                            variant="primary"
                            type={ButtonTypes.BUTTON}
                            onClick={handleSubmit}
                            label={resources.common.buttons.save.label}
                        />
                    )}
                </div>
            </div>
            <TabContainer>
                {/* @ts-ignore */}
                <Tabs id="relying-party-details" fill={true} unmountOnExit={true}>
                    <Tab eventKey={"relying-party-details-main"} title={relyingPartyDetailsValidationErrors.generalInfo.tabTitle}>
                        <RelyingPartyDetailsGeneralTab
                            axnCompanies={axnCompanies}
                            requestInProgress={requestInProgress}
                            labelWidth={labelWidth}
                            validationErrors={relyingPartyDetailsValidationErrors.generalInfo}
                            state={relyingPartyState}
                            setState={setRelyingPartyState}
                        />
                    </Tab>
                    {relyingParty.permissions.canViewCustomHostnames && (
                        <Tab eventKey={"relying-party-details-custom-hostnames"} title={relyingPartyDetailsValidationErrors.customHostnames.tabTitle}>
                            <RelyingPartyDetailsCustomHostnamesTab
                                labelWidth={labelWidth}
                                requestInProgress={requestInProgress}
                                validationErrors={relyingPartyDetailsValidationErrors.customHostnames}
                                state={relyingPartyState}
                                setState={setRelyingPartyState}
                            />
                        </Tab>
                    )}
                </Tabs>
            </TabContainer>
        </div>
    );
};
